import type { IssuedPassword } from '@obras/shared';
import { CircleCheck, Copy, KeyRound } from 'lucide-react';
import { useState } from 'react';

import { cn } from '../../lib/cn.js';
import { Button } from './Button.js';

const ICON = { strokeWidth: 1.5 } as const;

/**
 * O valor da senha, para ler em voz alta ou copiar.
 *
 * Separado da caixa inteira porque aparece em dois contextos: no cadastro, onde
 * vem acompanhado do aviso; e no painel da engrenagem, que já explicou tudo
 * acima e só precisa do valor.
 *
 * `surface` no botão porque o painel da engrenagem é `bg-muted`, onde o
 * `secondary` some — a cor de fundo vem por parâmetro pelo mesmo motivo.
 */
export function PasswordValue({
  password,
  onSurface = false,
}: {
  password: string;
  onSurface?: boolean;
}) {
  const [copiado, setCopiado] = useState(false);

  async function copiar(): Promise<void> {
    try {
      await navigator.clipboard.writeText(password);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Área de transferência bloqueada: a senha segue visível para leitura.
    }
  }

  return (
    <div className="flex items-center gap-2">
      <input
        readOnly
        value={password}
        onFocus={(event) => event.target.select()}
        aria-label="Senha definida"
        className={cn(
          'h-10 w-full min-w-0 rounded-md px-3 font-mono text-[15px] tracking-wide',
          onSurface ? 'bg-card' : 'bg-muted',
        )}
      />
      <Button
        variant={copiado ? 'success' : onSurface ? 'surface' : 'secondary'}
        icon={copiado ? <CircleCheck {...ICON} size={16} /> : <Copy {...ICON} size={16} />}
        onClick={() => void copiar()}
      >
        {copiado ? 'Copiada' : 'Copiar'}
      </Button>
    </div>
  );
}

/**
 * A senha entregue no cadastro, para o gestor ditar.
 *
 * Enquanto não há envio de e-mail (Fase 6), a senha sai em claro nesta tela e
 * em nenhuma outra: o banco guarda só o hash. Dizer "e-mail enviado" para um
 * e-mail que nunca sai seria pior.
 */
export function PasswordBox({ issued }: { issued: IssuedPassword }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg bg-primary-bg p-4">
        <p className="flex items-center gap-2 font-medium text-primary-strong">
          <KeyRound {...ICON} size={16} aria-hidden />
          Senha de {issued.email}
        </p>
        <p className="mt-1 text-[13px] text-primary-strong">
          Diga esta senha à pessoa e peça que ela troque no primeiro acesso. É a mesma para todo
          mundo — quem souber o padrão pode tentá-la em qualquer conta que ainda não a trocou.
        </p>
      </div>

      <PasswordValue password={issued.password} />
    </div>
  );
}
